import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, Loader2, Sparkles, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { loginAdmin, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user.role === "admin") {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError("Please fill in all fields");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await loginAdmin(email, password);
      toast.success("Welcome back!");
      navigate("/admin/dashboard");
    } catch (err) {
      const message = err.response?.data?.detail;
      const errorMsg = typeof message === 'string' ? message : 
        Array.isArray(message) ? message.map(e => e.msg || e).join(", ") : 
        "Invalid credentials";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (user && user.role === "admin") return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
      <div className="fixed inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(-45deg, #0A0A0A 0, #0A0A0A 1px, transparent 0, transparent 50%)`,
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="w-full max-w-md relative">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-[#4A4A4A] hover:text-[#0A0A0A] transition-colors mb-8"
          data-testid="back-home-btn"
        >
          <Sparkles className="w-5 h-5" />
          <span className="font-medium">Virtually Verbal</span>
        </button>

        <div className="brutalist-card p-10 animate-slide-up">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#FBE7C6] border-3 border-[#0A0A0A] flex items-center justify-center mx-auto mb-4" style={{ boxShadow: '4px 4px 0px 0px rgba(10,10,10,1)' }}>
              <LogIn className="w-8 h-8" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold">Admin Login</h1>
            <p className="text-[#4A4A4A] mt-1">Access the dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-bold uppercase tracking-wider mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@virtuallyverbal.com"
                className="brutalist-input w-full"
                disabled={loading}
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-bold uppercase tracking-wider mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="brutalist-input w-full pr-12"
                  disabled={loading}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A4A4A] hover:text-[#0A0A0A]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-[#FF6B6B] font-medium" data-testid="login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="brutalist-btn-primary w-full flex items-center justify-center gap-2"
              data-testid="login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
